// What an item is, opened by the (i) on its row: the curated sentence, then what the
// library says, then every usage in full, including the one the row already states.

import { dollars_label } from "../vendor/web";
import { price } from "./cart";
import type { BakedRow } from "./catalogue";
import { usageLines, usageText } from "./usages";

export function itemDetail(row: BakedRow): HTMLElement {
  const detail = document.createElement("div");
  detail.className = "item-detail";

  const blurb = written(row["blurb"]);
  if (blurb !== null) {
    const opening = document.createElement("p");
    opening.className = "item-prose item-blurb";
    opening.textContent = blurb;
    detail.append(opening);
  }

  const prose = written(row["local_notes"]);
  if (prose !== null) {
    const note = document.createElement("p");
    note.className = "item-prose";
    note.textContent = prose;
    detail.append(note);
  }

  const usages = usageLines(row);
  if (usages.length > 0) {
    const list = document.createElement("ul");
    list.className = "item-usages";
    for (const usage of usages) {
      const line = document.createElement("li");
      line.className = usage.level === null ? "item-usage item-usage-unskilled" : "item-usage";
      line.dataset["usage"] = usage.name;
      line.textContent = usageText(usage);
      list.append(line);
    }
    detail.append(list);
  }

  const facts = document.createElement("dl");
  facts.className = "item-facts";
  for (const [label, value] of readings(row)) {
    const term = document.createElement("dt");
    term.textContent = label;
    const reading = document.createElement("dd");
    reading.textContent = value;
    facts.append(term, reading);
  }
  detail.append(facts);
  return detail;
}

/** GCS carries no prose for well over half the catalogue, so weight and value close the
 *  list, and a row with nothing written about it still says what it is. */
function readings(row: BakedRow): [string, string][] {
  const out: [string, string][] = [];
  const stock = written(row.stock_description);
  if (stock !== null) out.push(["Library name", stock]);
  const reference = written(row["reference"]);
  if (reference !== null) out.push(["Reference", reference]);
  const tech = written(row["tech_level"]);
  if (tech !== null) out.push(["Tech level", tech]);
  const legality = written(row["legality_class"]);
  if (legality !== null) out.push(["Legality class", legality]);

  const tags = row["tags"];
  const named = Array.isArray(tags) ? tags.filter((tag) => typeof tag === "string") : [];
  if (named.length > 0) out.push(["Tags", named.join(", ")]);

  out.push(["Weight, each", row.calc.weight ?? "-"]);
  out.push(["Value, each", dollars_label(price(row, 1))]);
  return out;
}

function written(value: unknown): string | null {
  if (typeof value === "number") return String(value);
  if (typeof value !== "string" || value.trim() === "") return null;
  return value;
}
